/**
 * Prompt Input API Service
 * Handles all API calls related to prompt submission and file management
 */

const promptInputService = {
  getUiSpecRendererBaseUrl: () => {
    return process.env.REACT_APP_UI_SPEC_RENDERER_URL || '';
  },

  /**
   * Send a prompt to the renderer service
   * @param {string} input - The prompt text
   * @param {File[]} files - Array of uploaded files
   * @returns {Promise<Object>} - Response from the server
   */
  sendPrompt: async (input, files, styleRefFile = null) => {
    try {
      const baseUrl = promptInputService.getUiSpecRendererBaseUrl();
      const FILE_TYPES = ['image/', 'application/pdf', 'text/html', 'application/xhtml+xml'];

      const isJsFile = (f) =>
        f?.name?.endsWith('.js') || f?.name?.endsWith('.cjs') ||
        (f?.type || '').includes('javascript');

      const tailwindFile = (files || []).find(isJsFile);
      const uploadedFile = (files || []).find((f) =>
        !isJsFile(f) && FILE_TYPES.some((t) => f?.type?.startsWith(t))
      );

      // If a new JS config was uploaded, save it to sessionStorage for this session
      let tailwindConfigText = null;
      if (tailwindFile) {
        tailwindConfigText = await tailwindFile.text();
        sessionStorage.setItem('tailwindConfig', tailwindConfigText);
      } else {
        // Reuse the config from this session if one was previously uploaded
        tailwindConfigText = sessionStorage.getItem('tailwindConfig') || null;
      }

      // Style reference image always forces multipart
      const needsMultipart = !!(uploadedFile || styleRefFile);

      let response;
      if (needsMultipart) {
        const formData = new FormData();
        if (input) formData.append('prompt', input);
        if (uploadedFile) formData.append('file', uploadedFile);
        if (styleRefFile) formData.append('styleFile', styleRefFile);
        if (tailwindConfigText && !styleRefFile) formData.append('tailwindConfig', tailwindConfigText);

        response = await fetch(`${baseUrl}/api/generate-spec`, {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await fetch(`${baseUrl}/api/generate-spec`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: input || (tailwindFile ? 'Generate a comprehensive UI using the uploaded design system.' : undefined),
            tailwindConfig: tailwindConfigText,
          }),
        });
      }

      if (!response.ok) {
        let detail = '';
        try {
          const errJson = await response.json();
          detail = errJson?.detail || errJson?.message || '';
        } catch (_err) {
          detail = response.statusText;
        }
        throw new Error(`Failed to generate spec: ${detail || 'Unknown error'}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending prompt:', error);
      throw error;
    }
  },

  getActiveSpec: async () => {
    const baseUrl = promptInputService.getUiSpecRendererBaseUrl();
    const response = await fetch(`${baseUrl}/api/spec`, {
      method: 'GET',
    });

    if (!response.ok) {
      let detail = '';
      try {
        const errJson = await response.json();
        detail = errJson?.detail || errJson?.message || '';
      } catch (_err) {
        detail = response.statusText;
      }

      throw new Error(`Failed to fetch active spec: ${detail || 'Unknown error'}`);
    }

    return response.json();
  },

  /**
   * Validate uploaded files
   * @param {File[]} files - Array of files to validate
   * @returns {Object} - Validation result with isValid flag and errors array
   */
  validateFiles: (files) => {
    const result = {
      isValid: true,
      errors: [],
    };

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    const ALLOWED_TYPES = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/webp',
      'text/html',
      'application/xhtml+xml',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/javascript',
      'application/javascript',
      'application/x-javascript',
    ];

    const isJsFile = (file) =>
      file.name.endsWith('.js') || file.name.endsWith('.cjs') ||
      file.type.includes('javascript');

    files.forEach((file) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        result.isValid = false;
        result.errors.push(`${file.name} exceeds maximum file size of 50MB`);
      }

      // Check file type (JS files are allowed for Tailwind config uploads)
      if (!ALLOWED_TYPES.includes(file.type) && !isJsFile(file)) {
        result.isValid = false;
        result.errors.push(`${file.name} has unsupported file type`);
      }
    });

    return result;
  },

  /**
   * Get file upload progress
   * @returns {Promise<Object>} - Upload progress information
   */
  getUploadProgress: async () => {
    try {
      const response = await fetch('/api/prompt/upload-progress', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get upload progress');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting upload progress:', error);
      throw error;
    }
  },
};

export default promptInputService;
